import { useEffect, useRef, useState } from "react";
import { motion, useMotionValueEvent, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";
import { Toaster } from "react-hot-toast";
import "./index.css";
import { BookingContact, CallToAction, Footer, Hero, MessageWidget, SocialLinks, SpotifyEmbed } from "./components";
import { loadCmsContent } from "./lib/cms";
import { MEDIA_COMPACT, MEDIA_MOBILE } from "./lib/breakpoints";
import { DESKTOP_SCROLL_TIMELINE, MOBILE_SCROLL_TIMELINE, useMediaQuery, type TimelineStage } from "./lib/parallax";
import { defaultSiteContent } from "./siteData";

export function App() {
  const [content, setContent] = useState(defaultSiteContent);
  const [activeStage, setActiveStage] = useState<TimelineStage | null>(null);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [timelineProgress, setTimelineProgress] = useState(0);
  const timelineRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const isCompact = useMediaQuery(MEDIA_COMPACT);
  const isMobile = useMediaQuery(MEDIA_MOBILE);
  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ["start start", "end end"],
  });
  const smoothScrollYProgress = useSpring(scrollYProgress, {
    stiffness: isMobile ? 140 : 95,
    damping: isMobile ? 34 : 30,
    mass: isMobile ? 0.18 : 0.28,
    restDelta: 0.001,
  });
  const timeline = isMobile ? MOBILE_SCROLL_TIMELINE : DESKTOP_SCROLL_TIMELINE;
  const ctaReveal = isCompact ? timeline.compactCtaReveal : timeline.stages.cta.reveal;
  const socialReveal = timeline.stages.social.reveal;
  const spotifyReveal = timeline.stages.spotify.reveal;
  const contactReveal = timeline.stages.contact.reveal;
  const finalTitleReveal = timeline.finalTitleReveal;
  const isInRange = (value: number, start: number, end: number) => value >= start && value <= end;
  const ctaActiveWindow: [number, number] = isCompact ? [0, timeline.compactCtaReveal[2]] : timeline.stages.cta.active;
  const ctaInteractiveWindow: [number, number] = isCompact ? [0, timeline.compactCtaReveal[1]] : [timeline.stages.cta.reveal[1], timeline.stages.cta.reveal[2]];
  const socialInteractiveWindow: [number, number] = timeline.stages.social.pointer;
  const spotifyInteractiveWindow: [number, number] = timeline.stages.spotify.pointer;
  const contactInteractiveWindow: [number, number] = timeline.stages.contact.pointer;

  const heroImageScale = useTransform(
    smoothScrollYProgress,
    timeline.heroZoom,
    prefersReducedMotion ? [1, 1] : isMobile ? [1.08, 1] : [1.24, 1],
  );
  const heroImageOpacity = useTransform(smoothScrollYProgress, [0, 0.28, 0.48], prefersReducedMotion ? [1, 1, 1] : [1, 0.96, 0.62]);
  const heroTitleOpacity = useTransform(
    smoothScrollYProgress,
    timeline.titleReveal,
    prefersReducedMotion ? [1, 1, 1, 1] : [1, 1, 0.92, 0],
  );
  const heroTitleY = useTransform(
    smoothScrollYProgress,
    timeline.titleReveal,
    prefersReducedMotion ? [0, 0, 0, 0] : isMobile ? [10, 0, 0, -8] : [24, 0, 0, -44],
  );
  const ctaOpacity = useTransform(
    smoothScrollYProgress,
    timeline.stages.cta.reveal,
    prefersReducedMotion || isCompact ? [1, 1, 1, 0] : [0, 1, 1, 0],
  );
  const ctaY = useTransform(
    smoothScrollYProgress,
    timeline.stages.cta.reveal,
    prefersReducedMotion
      ? [0, 0, 0, 0]
      : isMobile
        ? [0, 0, -8, -34]
        : isCompact
          ? [0, 0, -8, -32]
          : [96, 0, -12, -48],
  );
  const ctaScale = useTransform(
    smoothScrollYProgress,
    timeline.stages.cta.reveal,
    prefersReducedMotion || isCompact ? [1, 1, 1, 0.96] : [0.92, 1, 1, 0.96],
  );
  const compactCtaOpacity = useTransform(smoothScrollYProgress, timeline.compactCtaReveal, prefersReducedMotion ? [1, 1, 1] : [1, 1, 0]);
  const compactCtaY = useTransform(smoothScrollYProgress, timeline.compactCtaReveal, prefersReducedMotion ? [0, 0, 0] : isMobile ? [0, -4, -16] : [0, -8, -32]);
  const compactCtaScale = useTransform(smoothScrollYProgress, timeline.compactCtaReveal, prefersReducedMotion ? [1, 1, 1] : [1, 1, 0.96]);

  const socialOpacity = useTransform(smoothScrollYProgress, socialReveal, prefersReducedMotion ? [1, 1, 1, 1] : [0, 1, 1, 0]);
  const socialY = useTransform(
    smoothScrollYProgress,
    socialReveal,
    prefersReducedMotion ? [0, 0, 0, 0] : isMobile ? [44, 32, 32, 26] : [106, 32, 22, -12],
  );
  const socialPointerEvents = useTransform(smoothScrollYProgress, value =>
    prefersReducedMotion || isInRange(value, socialInteractiveWindow[0], socialInteractiveWindow[1]) ? "auto" : "none",
  );
  const spotifyOpacity = useTransform(smoothScrollYProgress, spotifyReveal, prefersReducedMotion ? [1, 1, 1, 1] : [0, 1, 1, 0]);
  const spotifyY = useTransform(
    smoothScrollYProgress,
    spotifyReveal,
    prefersReducedMotion || isMobile ? [0, 0, 0, 0] : [92, 0, -14, -56],
  );

  const contactOpacity = useTransform(smoothScrollYProgress, contactReveal, prefersReducedMotion ? [1, 1, 1, 1] : [0, 1, 1, 0]);
  const contactY = useTransform(
    smoothScrollYProgress,
    contactReveal,
    prefersReducedMotion ? [0, 0, 0, 0] : isMobile ? [8, -12, -12, -20] : [18, -16, -16, -30],
  );
  const contactPointerEvents = useTransform(smoothScrollYProgress, value =>
    prefersReducedMotion || isInRange(value, contactInteractiveWindow[0], contactInteractiveWindow[1]) ? "auto" : "none",
  );
  const finalTitleOpacity = useTransform(smoothScrollYProgress, finalTitleReveal, [0, 1, 1]);
  const finalTitleY = useTransform(smoothScrollYProgress, finalTitleReveal, prefersReducedMotion ? [0, 0, 0] : isMobile ? [-130, 0, 0] : [-240, 0, 0]);
  const stageIsActive = (value: number, stage: TimelineStage) => {
    const [start, end] = stage === "cta" ? ctaActiveWindow : timeline.stages[stage].active;
    return isInRange(value, start, end);
  };

  useMotionValueEvent(smoothScrollYProgress, "change", value => {
    setTimelineProgress(value);

    if (stageIsActive(value, "contact")) {
      setActiveStage("contact");
    } else if (stageIsActive(value, "spotify")) {
      setActiveStage("spotify");
    } else if (stageIsActive(value, "social")) {
      setActiveStage("social");
    } else if (stageIsActive(value, "cta")) {
      setActiveStage("cta");
    } else {
      setActiveStage(null);
    }
  });

  const activeClass = (stage: "cta" | "social" | "spotify" | "contact") =>
    prefersReducedMotion || activeStage === stage || (stage === "cta" && isCompact && activeStage !== "social" && activeStage !== "spotify" && activeStage !== "contact")
      ? " timelinePanel--active"
      : "";
  const interactiveClass = (window: [number, number]) => (prefersReducedMotion || isInRange(timelineProgress, window[0], window[1]) ? " timelinePanel--interactive" : "");
  const midpoint = ([start, end]: [number, number]) => start + (end - start) / 2;
  const navigationItems: Array<{ label: string; progress: number; stage: TimelineStage | "home" }> = [
    { label: "Home", progress: 0, stage: "home" },
    { label: "Connect", progress: midpoint(ctaActiveWindow), stage: "cta" },
    { label: "Social", progress: midpoint(timeline.stages.social.active), stage: "social" },
    { label: "Music", progress: midpoint(timeline.stages.spotify.active), stage: "spotify" },
    { label: "Bookings", progress: midpoint(timeline.stages.contact.active), stage: "contact" },
  ];
  const getActiveNavigationStage = (): TimelineStage | "home" => {
    if (activeStage) {
      return activeStage;
    }

    if (timelineProgress >= timeline.stages.contact.active[0]) {
      return "contact";
    }

    if (timelineProgress >= timeline.stages.spotify.active[0]) {
      return "spotify";
    }

    if (timelineProgress >= timeline.stages.social.active[0]) {
      return "social";
    }

    return timelineProgress >= ctaActiveWindow[0] ? "cta" : "home";
  };
  const activeNavigationStage = getActiveNavigationStage();
  const scrollToTimelineProgress = (progress: number) => {
    const timelineElement = timelineRef.current;

    if (!timelineElement) {
      return;
    }

    const timelineTop = timelineElement.getBoundingClientRect().top + window.scrollY;
    const maxTimelineScroll = Math.max(0, timelineElement.offsetHeight - window.innerHeight);
    const top = timelineTop + maxTimelineScroll * progress;

    window.scrollTo({
      top,
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });
  };
  const handleNavigationClick = (progress: number) => {
    setMenuIsOpen(false);
    scrollToTimelineProgress(progress);
  };

  useEffect(() => {
    let isMounted = true;

    loadCmsContent().then(nextContent => {
      if (!isMounted) {
        return;
      }

      setContent(nextContent);
      document.title = nextContent.meta.title;
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!menuIsOpen) {
      return;
    }

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuIsOpen(false);
      }
    };

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [menuIsOpen]);

  return (
    <div className="siteShell">
      <nav className={`hamburgerNav${menuIsOpen ? " hamburgerNav--open" : ""}`} aria-label="Site sections">
        <button
          className="hamburgerNav__toggle"
          type="button"
          aria-label={menuIsOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuIsOpen}
          aria-controls="site-section-menu"
          onClick={() => setMenuIsOpen(current => !current)}
        >
          <span className="hamburgerNav__line" aria-hidden="true" />
          <span className="hamburgerNav__line" aria-hidden="true" />
          <span className="hamburgerNav__line" aria-hidden="true" />
        </button>
        <div className="hamburgerNav__scrim" aria-hidden="true" onClick={() => setMenuIsOpen(false)} />
        <div className="hamburgerNav__panel" id="site-section-menu">
          <span className="hamburgerNav__eyebrow">Navigate</span>
          <div className="hamburgerNav__links">
            {navigationItems.map(item => (
              <button
                className={`hamburgerNav__link${activeNavigationStage === item.stage ? " hamburgerNav__link--active" : ""}`}
                type="button"
                aria-current={activeNavigationStage === item.stage ? "page" : undefined}
                key={item.label}
                onClick={() => handleNavigationClick(item.progress)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </nav>
      <div className="scrollTimeline" ref={timelineRef}>
        <div className="timelineSticky">
          <Hero
            images={content.images}
            meta={content.meta}
            imageStyle={{ scale: heroImageScale, opacity: heroImageOpacity }}
            titleStyle={{ opacity: heroTitleOpacity, y: heroTitleY }}
          />
          <main className={`timelinePanels${isMobile && activeStage === "spotify" ? " timelinePanels--spotifyExclusive" : ""}`}>
            <motion.div
              style={{
                opacity: isCompact ? compactCtaOpacity : ctaOpacity,
                y: isCompact ? compactCtaY : ctaY,
                scale: isCompact ? compactCtaScale : ctaScale,
              }}
              className={`timelinePanel timelinePanel--cta${activeClass("cta")}${interactiveClass(ctaInteractiveWindow)}`}
            >
              <CallToAction cta={content.cta} />
            </motion.div>
            <motion.div
              style={{ opacity: socialOpacity, y: socialY, pointerEvents: socialPointerEvents }}
              className={`timelinePanel timelinePanel--social${activeClass("social")}${interactiveClass(socialInteractiveWindow)}`}
            >
              <div className="socialStack">
                <SocialLinks links={content.socialLinks} />
              </div>
            </motion.div>
            <motion.div
              style={{ opacity: spotifyOpacity, y: spotifyY }}
              className={`timelinePanel timelinePanel--spotify${activeClass("spotify")}${interactiveClass(spotifyInteractiveWindow)}`}
            >
              <SpotifyEmbed spotify={content.spotify} />
            </motion.div>
            <motion.div
              style={{ opacity: contactOpacity, y: contactY, pointerEvents: contactPointerEvents }}
              className={`timelinePanel timelinePanel--bookingContact${activeClass("contact")}${interactiveClass(contactInteractiveWindow)}`}
            >
              <BookingContact heading={content.contact.heading} image={content.images.contact} emails={content.contact.emails} />
            </motion.div>
            <motion.div className="timelinePanel timelinePanel--finalTitle" style={{ opacity: finalTitleOpacity, y: finalTitleY }}>
              <img className="finalTitle__logo" src={content.images.logo} alt={content.meta.author} />
            </motion.div>
          </main>
        </div>
      </div>
      <MessageWidget isHidden={menuIsOpen || (isMobile && timelineProgress <= ctaInteractiveWindow[1])} />
      <Toaster
        position="top-center"
        containerStyle={{ top: "max(10px, env(safe-area-inset-top))" }}
        toastOptions={{
          duration: 4200,
          style: {
            border: "1px solid rgb(255 255 255 / 0.16)",
            borderRadius: "999px",
            background: "linear-gradient(135deg, rgb(18 18 18 / 0.94), rgb(0 0 0 / 0.82))",
            boxShadow: "0 22px 70px rgb(0 0 0 / 0.42), inset 0 1px 0 rgb(255 255 255 / 0.12)",
            color: "#fff",
            fontFamily: "Helvetica, Arial, sans-serif",
            fontSize: "13px",
            fontWeight: 700,
            letterSpacing: "0.02em",
            padding: "12px 18px",
          },
          success: {
            iconTheme: {
              primary: "#fff",
              secondary: "#000",
            },
          },
          error: {
            iconTheme: {
              primary: "#ffb6b6",
              secondary: "#000",
            },
          },
        }}
      />
      <Footer footer={content.footer} />
    </div>
  );
}

export default App;
